/**
 * Provides functionality to generate a system block diagram as an svg
 * image.
 *
 * The diagram optionally has its own set of menu buttons, including zoom and save
 * functionality. To handle save events, set the onSave property to a callback
 * function of your choice.
 *
 * After creation of a new SystemBlockDiagram object, data has to be set with
 * setDiagramData() before the diagram can be rendered into a DOM element
 * with render().
 *
 * Internally, the d3 library is used to draw units, ports, and port
 * connections. To facilitate drawing, references are added to the provided
 * data, i.e. the diagram data is muted. The added references are removed
 * before the onSave() callback is invoked.
 *
 * @param d3 the d3 library object
 * @constructor
 */
function SystemBlockDiagram(d3) {

  this.d3 = d3;

  this.autoSetPortLocationsEnabled = false;
  this.panEnabled = false;

  this.diagramData = null;
  this.units = null;
  this.portConnections = null;

  this.viewBox = null;

  this.virtualPortConnectionWidth = 70;
  this.virtualPortConnectionHeight = 50;

  this.drawer = null;
  this.helper = new SbdHelper();

  // Set to false if the on-diagram menu should not be rendered
  this.renderMenu = true;
  this.menu = new SbdMenu(this);

  this.onSave = null;
  this.onSetDirty = null;
  this.onPortConnectionDblClick = null;
  this.onPortDblClick = null;
  this.onUnitDblClick = null;
}

/**
 * Invokes the callback to handle a save event. Before invokation, all added
 * references to the diagram data are removed to avoid any side-effects.
 * Afterwards, the references are restored.
 */
SystemBlockDiagram.prototype.save = function () {
  if (this.onSave !== null) {
    this.removeReferences();
    this.removeShortcutReferences();
    this.onSave();
    this.addShortcutReferences();
    this.addReferences();
  }
};

SystemBlockDiagram.prototype.unitDblClick = function(unit) {
  if (this.onUnitDblClick !== null) {
    this.onUnitDblClick(unit);
  }
};

SystemBlockDiagram.prototype.portDblClick = function(port) {
  if (this.onPortDblClick !== null) {
    this.onPortDblClick(port);
  }
};

SystemBlockDiagram.prototype.portConnectionDblClick = function(portConnection) {
  if (this.onPortConnectionDblClick !== null) {
    this.onPortConnectionDblClick(portConnection);
  }
};

/**
 * Marks the given entity as dirty and calls the event handler (if set).
 *
 * @param entity
 */
SystemBlockDiagram.prototype.setDirty = function(entity) {
  entity['dirty'] = true;
  if (this.onSetDirty !== null) {
    this.onSetDirty();
  }
};

/**
 * If set to true, port locations will be set automatically on unit
 * location update in a way that:
 * - the connecting line between the ports is straight
 * - the port of the unit with the lower "level" value is centered
 *   on the edge
 *
 * @param enabled
 */
SystemBlockDiagram.prototype.enableAutoSetPortLocations = function (enabled) {
  this.autoSetPortLocationsEnabled = enabled;
  if (this.autoSetPortLocationsEnabled) {
    this.autoSetPortLocations();
    this.drawer.updateUnits(this.units, this.portConnections);
  }
  if (this.renderMenu) {
    this.menu.autoSetPortLocationsButton.pressed = enabled;
    this.menu.updateButtonStates();
  }
};

/**
 * If set to true, all units can be moved at once by dragging the mouse on free
 * diagram space.
 *
 * @param enabled
 */
SystemBlockDiagram.prototype.setPanEnabled = function (enabled) {
  this.panEnabled = enabled;
};

/**
 * Callback for port drag events.
 *
 * @param port
 * @param x
 * @param y
 */
SystemBlockDiagram.prototype.onDragPort = function (port, x, y) {
  var newCoords = this.helper.nearestOnRect(x, y,
    0, 0, port.parent.width, port.parent.height);
  port.x = Math.round(newCoords.x);
  port.y = Math.round(newCoords.y);
  this.setDirty(port);

  this.drawer.updatePortLocations(this.units, this.portConnections);
  this.drawer.updateUnitIconOrientation(this.units);
};

/**
 * Callback for unit drag events.
 *
 * @param unit
 * @param x
 * @param y
 */
SystemBlockDiagram.prototype.onDragUnit = function (unit, x, y) {
  unit.x = Math.round(x);
  unit.y = Math.round(y);

  if (this.autoSetPortLocationsEnabled) {
    this.autoSetPortLocations();
  }
  this.drawer.updateUnits(this.units, this.portConnections);
};

/**
 * Callback for unit drag end events.
 *
 * @param unit
 */
SystemBlockDiagram.prototype.onDragUnitEnd = function (unit) {
  this.setDirty(unit);
  this.resizeSvgToContent();
};

/**
 * Callback for drag events of unit resize handles.
 *
 * @param unit
 * @param dx
 * @param dy
 */
SystemBlockDiagram.prototype.onDragUnitResizeHandle = function (unit, dx, dy) {
  if (unit.width + dx < 10 || unit.height + dy < 10) {
    return;
  }

  for (var i = 0; i < unit.diagram_ports.length; i++) {
    var p = unit.diagram_ports[i];
    if (p.x === unit.width) {
      p.x += dx;
      this.setDirty(p);
    }
    if (p.y === unit.height) {
      p.y += dy;
      this.setDirty(p);
    }
  }

  unit.width += dx;
  unit.height += dy;

  if (this.autoSetPortLocationsEnabled) {
    this.autoSetPortLocations();
  }
  this.drawer.updateUnits(this.units, this.portConnections);
};

/**
 * Callback for drag end evends of unit resize handles.
 *
 * @param unit
 */
SystemBlockDiagram.prototype.onDragUnitResizeHandleEnd = function (unit) {
  unit.width = Math.round(unit.width);
  unit.height = Math.round(unit.height);
  this.setDirty(unit);

  for (var i = 0; i < unit.diagram_ports.length; i++) {
    var p = unit.diagram_ports[i];
    if (p.x !== Math.round(p.x) || p.y !== Math.round(p.y)) {
      p.x = Math.round(p.x);
      p.y = Math.round(p.y);
      this.setDirty(p);
    }
  }

  this.resizeSvgToContent();
};

/**
 * Resizes the svg to fit the contents.
 */
SystemBlockDiagram.prototype.resizeSvgToContent = function () {
  var maxCoords = {x: 1, y: 1};
  for (var i = 0; i < this.units.length; i++) {
    var unitEdge = {
      x: this.units[i].x + this.units[i].width,
      y: this.units[i].y + this.units[i].height
    };
    if (this.units[i].diagram_ports.find(function (p) {
        return p.connected_to_virtual_port;
      })) {
      unitEdge.y += 50;
      unitEdge.x += 50;
    }
    maxCoords.x = maxCoords.x > unitEdge.x ? maxCoords.x : unitEdge.x;
    maxCoords.y = maxCoords.y > unitEdge.y ? maxCoords.y : unitEdge.y;
  }

  this.diagramData.width = Math.max((maxCoords.x * this.diagramData.zoom_factor) + 20,
    this.menu.bounds.x + 20, 200);
  this.diagramData.height = Math.max((maxCoords.y * this.diagramData.zoom_factor) + 20,
    this.menu.bounds.y + 20, 50);
  this.diagramData.width = Math.round(this.diagramData.width);
  this.diagramData.height = Math.round(this.diagramData.height);
  this.svg.attr('width', this.diagramData.width);
  this.svg.attr('height', this.diagramData.height);
  this.updateViewBox();
};

/**
 * Renders the svg as a child of the provided DOM element.
 *
 * @param parentElement the DOM element
 */
SystemBlockDiagram.prototype.render = function (parentElement) {
  var that = this;
  this.svg = this.d3.select(parentElement)
    .append('svg')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
    .attr('class', 'system-block-diagram')
    .attr("width", this.diagramData.width)
    .attr("height", this.diagramData.height)
    .style("padding", "10px")
    .call(this.d3.drag()
      .on('drag', function () {
        if (that.panEnabled) {
          that.panDrag();
        }
      })
      .on('end', function () {
        if (that.panEnabled) {
          that.endPanDrag();
        }
      }));
  this.viewBox = {
    x0: 0,
    y0: 0,
    x1: this.diagramData.width,
    y1: this.diagramData.height
  };

  if (this.renderMenu) {
    this.menu.render();
  }
  this.zoom(this.diagramData.zoom_factor);

  if (this.autoSetPortLocationsEnabled) {
    this.autoSetPortLocations();
  }

  this.drawer = new SbdDrawer(this, this.svg, this.d3);
  this.drawer.drawUnits(this.units);
  this.drawer.drawPortConnections(this.portConnections);
};

/**
 * Zooms in.
 */
SystemBlockDiagram.prototype.zoomIn = function () {
  this.zoom(this.diagramData.zoom_factor *= 1.05);
}

/**
 * Zooms out.
 */
SystemBlockDiagram.prototype.zoomOut = function () {
  this.zoom(this.diagramData.zoom_factor *= (1 / 1.05));
}

/**
 * Zooms to the given factor.
 *
 * @param factor
 */
SystemBlockDiagram.prototype.zoom = function (factor) {
  this.diagramData.zoom_factor = factor;
  this.updateViewBox();
  if (this.renderMenu) {
    this.menu.updatePositionAndSize(this.diagramData.zoom_factor);
  }
  this.resizeSvgToContent();
};

/**
 * Updates the svg viewbox according to the selected zoom factor.
 */
SystemBlockDiagram.prototype.updateViewBox = function () {
  this.viewBox.x1 = this.diagramData.width / this.diagramData.zoom_factor;
  this.viewBox.y1 = this.diagramData.height / this.diagramData.zoom_factor;
  this.svg.attr('viewBox', this.viewBox.x0 + ' '
    + ' ' + this.viewBox.y0
    + ' ' + this.viewBox.x1
    + ' ' + this.viewBox.y1);
};

/**
 * Pans the whole diagram. Current implementation moves all units by the given
 * delta x and y.
 *
 * @param panX
 * @param panY
 */
SystemBlockDiagram.prototype.pan = function (panX, panY) {
  for (var i = 0; i < this.units.length; i++) {
    if (Math.abs(panX) > 1 || Math.abs(panY) > 1) {
      this.units[i].x += panX;
      this.units[i].y += panY;
      this.setDirty(this.units[i]);
    }
  }
  this.drawer.updateUnits(this.units, this.portConnections);
  this.resizeSvgToContent();
};

/**
 * Callback for pan drag events.
 */
SystemBlockDiagram.prototype.panDrag = function () {
  this.pan(this.d3.event.dx,
    this.d3.event.dy);
};

/**
 * Callback for pan drag end events.
 */
SystemBlockDiagram.prototype.endPanDrag = function () {
  for (var i = 0; i < this.units.length; i++) {
    this.units[i].x = Math.round(this.units[i].x);
    this.units[i].y = Math.round(this.units[i].y);
  }
};

/**
 * Show or hide the gui zone for each unit.
 *
 * @param show true to show, false to hide
 */
SystemBlockDiagram.prototype.showGuiZones = function(show) {
  var guiZoneGetter = function(unit) {
    return unit.gui_zone_short_name;
  };
  this.drawer.showUnitInfo(this.units, guiZoneGetter, show);
};

/**
 * Computes the coordinates of a port (relativ to the unit) in a way that:
 * - The connection is either vertical or horizontal (if possible)
 * - The port on the unit with the lower 'level' property is centered on the
 *   respective unit edge.
 *
 * @param port
 * @returns {x, y}
 */
SystemBlockDiagram.prototype.getAutoLayoutPortCoords = function (port) {
  if (port.portConnections.length === 0) {
    return null;
  }

  var connectedUnit = port.portConnections[0].getConnectedUnit(port.parent);
  if (connectedUnit === null) {
    return null;
  }

  var isMaster = (connectedUnit.level === port.parent.level
    ? connectedUnit.id < port.parent.id
    : connectedUnit.level < port.parent.level);

  var masterUnit = isMaster ?
    port.parent : connectedUnit;
  var slaveUnit = isMaster ?
    connectedUnit : port.parent;

  // Calc offset for multiple connections to the same unit
  var portsToSameUnit = [];
  for (var i = 0; i < masterUnit.diagram_ports.length; i++) {
    var p = masterUnit.diagram_ports[i];
    if (p.portConnections.length > 0
      && p.portConnections[0].getConnectedUnit(masterUnit) === slaveUnit) {
      portsToSameUnit.push(isMaster ? p : p.portConnections[0].getConnectedPort(masterUnit));
    }
  }

  var offset = 0;
  if (portsToSameUnit.length > 1) {
    offset = portsToSameUnit.indexOf(port) * 20 - (portsToSameUnit.length - 1) * 10;
  }


  var absCoordsSlave = this.helper.nearestOnRect(masterUnit.x + masterUnit.width / 2 + offset,
    masterUnit.y + masterUnit.height / 2 + offset,
    slaveUnit.x, slaveUnit.y,
    slaveUnit.width, slaveUnit.height);
  var slaveUnitPortLoc = {
    x: absCoordsSlave.x - slaveUnit.x,
    y: absCoordsSlave.y - slaveUnit.y
  };
  var absCoordsMaster = this.helper.nearestOnRect(absCoordsSlave.x,
    absCoordsSlave.y,
    masterUnit.x, masterUnit.y,
    masterUnit.width, masterUnit.height);
  var masterUnitPortLoc = {
    x: absCoordsMaster.x - masterUnit.x,
    y: absCoordsMaster.y - masterUnit.y
  };
  return isMaster ?
    masterUnitPortLoc :
    slaveUnitPortLoc;
};

/**
 * Automatically sets the port locations (relativ to the units) as described
 * by getAutoLayoutPortCoords().
 */
SystemBlockDiagram.prototype.autoSetPortLocations = function () {
  for (var i = 0; i < this.units.length; i++) {
    for (var j = 0; j < this.units[i].diagram_ports.length; j++) {
      var port = this.units[i].diagram_ports[j];
      var portCoords = this.getAutoLayoutPortCoords(port);
      if (portCoords !== null && (port.x !== Math.round(portCoords.x)
          || port.y !== Math.round(portCoords.y))) {
        port.x = Math.round(portCoords.x);
        port.y = Math.round(portCoords.y);
      }
    }
  }
};

/**
 * Adds necessary object references to the diagram data to facilitate drawing.
 */
SystemBlockDiagram.prototype.addReferences = function () {
  var ports = {};

  // Create back references from port to unit and add array for port
  // connections
  for (var i = 0; i < this.units.length; i++) {
    for (var j = 0; j < this.units[i].diagram_ports.length; j++) {
      var port = this.units[i].diagram_ports[j];
      port['parent'] = this.units[i];
      port['portConnections'] = [];
      ports[port.id] = port;
    }
  }

  // Create references from port connections to ports and vice versa
  for (var i = 0; i < this.portConnections.length; i++) {
    var pc = this.portConnections[i];
    pc['sourcePort'] = ports[pc.source_port_id];
    pc['destPort'] = ports[pc.dest_port_id];
    ports[pc.source_port_id].portConnections.push(pc);
    ports[pc.dest_port_id].portConnections.push(pc);

    // Add convenience function to retrieve connected port
    pc['getConnectedPort'] = function (ownUnit) {
      if (this.sourcePort.parent === ownUnit) {
        return this.destPort;
      } else if (this.destPort.parent === ownUnit) {
        return this.sourcePort;
      } else {
        console.error('Port connection ' + this.id + ' does not contain unit ' + ownUnit.id);
        return null;
      }
    };

    // Add convenience function to retrieve connected unit
    pc['getConnectedUnit'] = function (ownUnit) {
      return this.getConnectedPort(ownUnit).parent;
    };
  }
};

/**
 * Removes the added references (see addReferences()) to clean up the diagram
 * data before a save event (the data is not owned by the SystemBlockDiagram
 * object).
 */
SystemBlockDiagram.prototype.removeReferences = function () {
  for (var i = 0; i < this.units.length; i++) {
    for (var j = 0; j < this.units[i].diagram_ports.length; j++) {
      var port = this.units[i].diagram_ports[j];
      delete port['parent'];
      delete port['portConnections'];
    }
  }

  // Delete references from port connections to ports and vice versa
  for (var i = 0; i < this.portConnections.length; i++) {
    var pc = this.portConnections[i];
    delete pc['sourcePort'];
    delete pc['destPort'];

    // Delete convenience function to retrieve connected unit
    delete pc['getConnectedUnit'];
  }
};

/**
 * Set units and port connections.
 *
 * @param units
 * @param portConnections
 */
SystemBlockDiagram.prototype.setData = function (units, portConnections) {
  this.units = units;
  this.portConnections = portConnections;

  this.addReferences();
};

/**
 * Set the diagram data. The data is expected to have the following format:
 * {
 *   zoom_factor: number,
 *   diagram_units: [{
 *      x: number,
 *      y: number,
 *      width: number,
 *      height: number,
 *      level: number,
 *      part_number: string,
 *      label: string,
 *      location_address: number,
 *      icon_id?: string,
 *      _embedded: {
 *        diagram_ports: [{
 *          x: number,
 *          y: number,
 *          type: string,
 *          name: string,
 *          connected_to_virtual_port: boolean,
 *          _embedded: {
 *            diagram_port_connections: [{
 *              source_port_id: number,
 *              dest_port_id: number,
 *              type: string
 *            },
 *            ...
 *            ]
 *          }
 *        },
 *        ...
 *        ]
 *      }
 *   },
 *   ...
 *   ]
 * }
 *
 * The data is muted as units or ports are moved.
 *
 * @param diagramData
 */
SystemBlockDiagram.prototype.setDiagramData = function (diagramData) {
  this.diagramData = diagramData;
  this.addShortcutReferences();
  this.setDiagramUnits(diagramData.diagram_units);
};

/**
 * Adds shortcut references to the diagram data to facilitate drawing. The
 * diagram data is expected to be in hal+json format, with embedded
 * objects stored in '_embedded' properties. For ease of use, direct links
 * from units to ports and ports to port connections are created.
 */
SystemBlockDiagram.prototype.addShortcutReferences = function () {
  if (this.diagramData._embedded && this.diagramData._embedded.diagram_units) {
    this.diagramData['diagram_units'] = this.diagramData._embedded.diagram_units;
  } else {
    this.diagramData['diagram_units'] = [];
  }
  for (var i = 0; i < this.diagramData.diagram_units.length; i++) {
    var unit = this.diagramData.diagram_units[i];
    if (unit._embedded && unit._embedded.diagram_ports) {
      unit['diagram_ports'] = unit._embedded.diagram_ports;
      for (var j = 0; j < unit._embedded.diagram_ports.length; j++) {
        var port = unit._embedded.diagram_ports[j];
        if (port._embedded && port._embedded.diagram_port_connections) {
          port['diagram_port_connections'] = port._embedded.diagram_port_connections;
        } else {
          port['diagram_port_connections'] = [];
        }
      }
    } else {
      unit['diagram_ports'] = [];
    }
  }
};

/**
 * Removes the shortcut references created with addShortcutReferences() to clean
 * up the data before save events.
 */
SystemBlockDiagram.prototype.removeShortcutReferences = function () {
  for (var i = 0; i < this.diagramData.diagram_units.length; i++) {
    var unit = this.diagramData.diagram_units[i];
    for (var j = 0; j < unit.diagram_ports.length; j++) {
      var port = unit.diagram_ports[j];
      delete port.diagram_port_connections;
    }
    delete unit.diagram_ports;
  }
  delete this.diagramData.diagram_units;
};

/**
 * Sets diagram units.
 * @param diagram_units
 */
SystemBlockDiagram.prototype.setDiagramUnits = function (diagram_units) {
  var portConnections = [];

  for (var i = 0; i < diagram_units.length; i++) {
    var unit = diagram_units[i];
    if (unit.diagram_ports) {
      for (var j = 0; j < unit.diagram_ports.length; j++) {
        var port = unit.diagram_ports[j];
        if (port.diagram_port_connections) {
          for (var p = 0; p < port.diagram_port_connections.length; p++) {
            portConnections.push(port.diagram_port_connections[p]);
          }
        }
      }
    }
  }

  this.setData(diagram_units, portConnections);
};

/**
 * Loads sample data for demonstration purposes.
 */
SystemBlockDiagram.prototype.loadSampleData = function () {
  var units = [
    {
      id: 'unit1',
      x: 100, y: 300, width: 500, height: 70,
      partnumber: "EIU3009-001-001",
      label: 'EIU3009',
      location_address: 1,
      level: 1,
      diagram_ports: [
        {
          id: 'unit1_port1',
          x: 150, y: 0,
          type: 'ethernet',
          name: 'J1'
        },
        {
          id: 'unit1_port2',
          x: 250, y: 0,
          type: 'discreteIn',
          name: 'J2'
        },
        {
          id: 'unit1_port3',
          x: 400, y: 0,
          type: 'ethernet',
          name: 'J3'
        }
      ]
    },
    {
      id: 'unit2',
      x: 200, y: 150, width: 200, height: 70,
      partnumber: "APA3012-001-001\r\nGUI Zone: 1",
      label: 'APA3012',
      location_address: 4,
      level: 2.01,
      diagram_ports: [
        {
          id: 'unit2_port1',
          x: 100, y: 70,
          type: 'ethernet',
          name: 'J4'
        },
        {
          id: 'unit2_port2',
          x: 200, y: 40,
          type: 'ethernet',
          name: 'J5'
        },
        {
          id: 'unit2_port3',
          x: 200, y: 60,
          type: 'usb',
          name: 'J6'
        },
        {
          id: 'unit2_port4',
          x: 160, y: 70,
          type: '28V',
          name: 'J6'
        }
      ]
    },
    {
      id: 'unit3',
      x: 500, y: 150, width: 200, height: 70,
      partnumber: "APA3012-001-001",
      label: 'APA3012',
      location_address: 5,
      level: 2.02,
      diagram_ports: [
        {
          id: 'unit3_port1',
          x: 100, y: 70,
          type: 'ethernet',
          name: 'J4'
        },
        {
          id: 'unit3_port2',
          x: 0, y: 40,
          type: 'ethernet',
          name: 'J5'
        },
        {
          id: 'unit3_port3',
          x: 0, y: 60,
          type: 'usb',
          name: 'J6'
        },
        {
          id: 'unit3_port4',
          x: 160, y: 70,
          type: '28V',
          name: 'J6'
        },
        {
          id: 'unit3_analogOut1',
          x: 30, y: 0,
          type: 'analogAudio',
          name: 'C1'
        }
      ]
    },
    {
      id: 'spk1',
      x: 300, y: 50, width: 100, height: 40,
      partnumber: "SPK1814",
      label: 'LS',
      icon_id: 'speaker',
      location_address: null,
      level: 3,
      diagram_ports: [
        {
          id: 'spk1_port1',
          x: 100, y: 40,
          type: 'analogAudio',
          name: ''
        }
      ]
    }
  ];

  var portConnections = [
    {
      source_port_id: 'unit1_port1',
      dest_port_id: 'unit2_port1',
      type: 'ethernet'
    },
    {
      source_port_id: 'unit1_port3',
      dest_port_id: 'unit3_port1',
      type: 'ethernet'
    },
    {
      source_port_id: 'unit2_port2',
      dest_port_id: 'unit3_port2',
      type: 'ethernet'
    },
    {
      source_port_id: 'unit3_analogOut1',
      dest_port_id: 'spk1_port1',
      type: 'analogaudio'
    },
    {
      source_port_id: 'unit3_analogOut1',
      dest_port_id: 'spk1_port1',
      type: 'analogaudio'
    },
    {
      source_port_id: 'unit2_port3',
      dest_port_id: 'unit3_port3',
      type: 'usb'
    },
    {
      source_port_id: 'unit2_port4',
      dest_port_id: 'unit3_port4',
      type: '28v'
    }
  ];

  this.diagramData = {
    width: 200,
    height: 200,
    zoom_factor: 1
  };
  this.setData(units, portConnections);
};

/**
 * Drawer object encapsulating the drawing functionality for a System Block
 * Diagram.
 *
 * @param sbd the SystemBlockDiagram
 * @param svg the svg element
 * @param d3 the d3 library object
 * @constructor
 */
function SbdDrawer(sbd, svg, d3) {
  this.sbd = sbd;
  this.svg = svg;
  this.d3 = d3;
  this.helper = new SbdHelper();

  this.PORT_LABEL_MIN_DIST = 8;

  this.addDefs();
  this.initLineMarker();
}

/**
 * Add svg definitions used for markers and icons.
 */
SbdDrawer.prototype.addDefs = function () {
  var defsElem = this.svg.append('defs');
  this.addStyleDef(defsElem);
  this.addMarkerDefs(defsElem);
  this.addIconDefs(defsElem);
};

SbdDrawer.prototype.addStyleDef = function (defsElem) {
  defsElem.append('style')
    .attr('type', 'text/css')
    .text(`
g.button-click rect {
  stroke: black;
  fill: #eeeeee;
}

g.button-click:hover rect {
  stroke: black;
  fill: #ffffff;
}

g.button-toggle rect {
  stroke: black;
  fill: #eeeeee;
}

g.button-pressed rect {
  fill: #00ff00;
}

g.button text {
  pointer-events: none;
}

text {
  font-family: verdana, arial;
  pointer-events: none;
}

circle.locationAddress {
  stroke:#006600;
  fill: yellow;
}

rect.unit {
  fill: #f6f6f6;
  stroke: black;
  stroke-width: 1;
}

rect.unit-external {
  stroke-dasharray: 5;
}

rect.unit:hover {
  stroke-width: 2;
}

text.unit-info {
  fill: grey;
}

g.speaker path {
  fill: #f6f6f6;
}

g.speaker:hover path {
  stroke-width: 2;
}

g.light path {
  fill: #f6f6f6;
  stroke-width: 1;
}

g.light:hover path {
  stroke-width: 2;
}

rect.resize-handle {
  opacity: 0;
}

rect.resize-handle:hover {
  cursor: w-resize;
}

g.portConnection rect {
  stroke:#000066;
  fill:#0000cc
}

rect.portConnection-editable:hover {
  stroke-width: 2;
}

g.portConnection-ethernet rect {
  stroke: black;
  fill: #99CCFF;
}

g.portConnection-usb rect {
  stroke: black;
  fill: #99CC00;
}

g.portConnection-usb text {
  fill: black;
  font-weight: normal;
}

g.portConnection-hdmi rect {
  fill: rgb(255,0,255);
  stroke: black;
}

g.portConnection-analogaudio rect {
  fill: rgb(192,192,192);
  stroke: black;
}

g.portConnection-audiopower rect {
  fill: rgb(192,192,192);
  stroke: black;
}

g.portConnection-canbus rect {
  fill: rgb(66,200,244);
  stroke: black;
}

g.portConnection-discrete rect {
  fill: white;
  stroke: black;
}

g.portConnection-resistor rect {
  fill: white;
  stroke: black;
}

g.portConnection-sdi rect {
  fill: white;
  stroke: black;
}

g.portConnection-serialvideo rect {
  fill: rgb(153,204,0);
  stroke: black;
}

g.portConnection-rs485 rect {
  fill: rgb(153,204,0);
  stroke: black;
}

g.portConnection-rs232 rect {
  fill: rgb(153,204,0);
  stroke: black;
}

g.portConnection-arinc429 rect {
  fill: white;
  stroke: black;
}

g.portConnection-hdbaset rect {
  fill: rgb(255,102,102);
  stroke: black;
}

g.portConnection-dc28v rect {
  fill: rgb(255,255,0);
  stroke: black;
}

text.portConnectionLabel {
  fill: black;
  stroke: none;
  font-weight: bold;
  font-size: 0.7em;
}

text.portConnectionLabel-analogaudio {

}

text.portConnectionLabel-audiopower {

}

text.portConnectionLabel-ethernet {
  font-size: 1em;
  font-weight: normal;
  fill: black;
}

text.portConnectionLabel-hdmi {
  //font-size: 1em;
  font-weight: bold;
  fill: white;
}

text.portConnectionLabel-canbus {

}

text.portLabel {
  fill: black;
  stroke: none;
  font-size: 0.8em;
}

text.icon-label-speaker {
  font-size: 0.8em;
}

rect.port {
  stroke: none;
  fill: white;
  fill-opacity: 0;
}

circle.port {
  fill-opacity: 0;
}

circle.port:hover {
  fill-opacity: 1;
}

g.port-drag circle {
  fill-opacity: 1;
}

rect.port-ethernet {
}

rect.port-usb {
}

rect.port-discreteIn {
}

rect.port-analogaudio {
}

rect.port-audiopower {
}

marker.arrow {
  fill: none;
  stroke: black;
}

marker.arrow-ethernet {
  stroke: #3366FF;
  fill: #3366FF;
}

marker.arrow-usb {
  stroke: #99CC00;
  fill: #99CC00;
}

marker.arrow-hdmi {
  fill: rgb(255,0,255);
  stroke: rgb(255,0,255);
}

marker.arrow-analogaudio {
  fill: black;
  stroke: black;
}

marker.arrow-audiopower {
  fill: black;
  stroke: black;
}

marker.arrow-discrete {
  fill: black;
  stroke: black;
}

marker.arrow-resistor {
  fill: black;
  stroke: black;
}

marker.arrow-sdi {
  fill: black;
  stroke: black;
}

marker.arrow-canbus {
  fill: black;
  stroke: black;
}

marker.arrow-serialvideo {
  fill: rgb(153,204,0);
  stroke: rgb(153,204,0);
}

marker.arrow-rs485 {
  fill: rgb(146,208,80);
  stroke: rgb(146,208,80);
}

marker.arrow-rs232 {
  fill: rgb(146,208,80);
  stroke: rgb(146,208,80);
}

marker.arrow-arinc429 {
  fill: black;
  stroke: black;
}

marker.arrow-hdbaset {
  fill: rgb(255,102,102);
  stroke: rgb(255,102,102);
}

marker.arrow-dc28v {
  fill: black;
  stroke: black;
}

line.portConnection {
  stroke:#222222;
}

line.portConnection-removed {
  stroke-dasharray: 2;
}

line.portConnectionVirtual {
  stroke:#222222;
}

line.portConnection-ethernet {
  stroke-width: 2;
  stroke: #3366FF;
  marker-start: url(#markerArrowStart-ethernet);
  marker-end: url(#markerArrowEnd-ethernet);
}

line.portConnection-usb {
  stroke-width: 2;
  stroke: #99CC00;
  marker-start: url(#markerArrowStart-usb);
  marker-end: url(#markerArrowEnd-usb);
}

line.portConnection-serialvideo {
  stroke: rgb(153,204,0);
  marker-start: url(#markerArrowStart-serialvideo);
  marker-end: url(#markerArrowEnd-serialvideo);
}

line.portConnection-analogaudio {
  marker-end: url(#markerArrowEnd-analogaudio);
}

line.portConnection-audiopower {
  marker-end: url(#markerArrowEnd-audiopower);
}

line.portConnection-discrete {
  marker-end: url(#markerArrowEnd-discrete);
}

line.portConnection-resistor {
  marker-end: url(#markerArrowEnd-resistor);
}

line.portConnection-sdi {
  marker-end: url(#markerArrowEnd-sdi);
}

line.portConnection-canbus {
  marker-end: url(#markerArrowEnd-canbus);
}

line.portConnection-hdmi {
  stroke: rgb(255,0,255);
  marker-end: url(#markerArrowEnd-hdmi);
}

line.portConnection-rs485 {
  stroke: rgb(146,208,80);
  marker-start: url(#markerArrowStart-rs485);
  marker-end: url(#markerArrowEnd-rs485);
}

line.portConnection-rs232 {
  stroke: rgb(146,208,80);
  marker-start: url(#markerArrowStart-rs232);
  marker-end: url(#markerArrowEnd-rs232);
}

line.portConnection-arinc429 {
  marker-end: url(#markerArrowEnd-arinc429);
}

line.portConnection-hdbaset {
  marker-end: url(#markerArrowEnd-hdbaset);
}

line.portConnection-dc28v {
  marker-end: url(#markerArrowEnd-dc28v);
}

path.speaker {
  stroke: black;
  fill: none;
}
`);
};

/**
 * Add marker definitions to the svg.
 *
 * @param defsElem
 */
SbdDrawer.prototype.addMarkerDefs = function (defsElem) {
  function createMarkerArrows(type) {
    // Start
    defsElem.append('marker')
      .attr('id', 'markerArrowStart-' + type)
      .attr('class', 'arrow arrow-' + type + ' arrowStart-' + type)
      .attr('markerWidth', 8)
      .attr('markerHeight', 6)
      .attr('refX', 0)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M8,0 L0,3 L8,6 Z');

    defsElem.append('marker')
      .attr('id', 'markerArrowEnd-' + type)
      .attr('class', 'arrow arrow-' + type + ' arrowEnd-' + type)
      .attr('markerWidth', 8)
      .attr('markerHeight', 6)
      .attr('refX', 8)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L8,3 L0,6 Z');
  }

  createMarkerArrows('ethernet');
  createMarkerArrows('usb');
  createMarkerArrows('hdmi');
  createMarkerArrows('analogaudio');
  createMarkerArrows('audiopower');
  createMarkerArrows('rs485');
  createMarkerArrows('rs232');
  createMarkerArrows('discrete');
  createMarkerArrows('resistor');
  createMarkerArrows('serialvideo');
  createMarkerArrows('arinc429');
  createMarkerArrows('sdi');
  createMarkerArrows('canbus');
  createMarkerArrows('dc28v');
  createMarkerArrows('hdbaset');
};

/**
 * Add icon definitions to the svg.
 *
 * @param defsElem
 */
SbdDrawer.prototype.addIconDefs = function (defsElem) {
  this.icons = {
    speaker: {
      defId: 'speakerDef',
      width: 40, height: 20,
      fixedPortLocation: {x: 20, y: 20},
      autoRotate: true
    },
    msp: {
      defId: 'mspDef',
      fixedPortLocation: null,
      autoRotate: false
    },
    light: {
      defId: 'lightDef',
      width: 48, height: 48,
      fixedPortLocation: {x: 24, y: 48},
      autoRotate: true,
      scale: 2
    }
  };

  for (var i = 0; i < this.sbd.units.length; i++) {
    var unit = this.sbd.units[i];
    if (this.isIconizedUnit(unit)) {
      var icon = this.getIcon(unit);
      unit['width'] = icon.width;
      unit['height'] = icon.height;
      unit['icon'] = icon;
      if (icon.fixedPortLocation !== null) {
        for (var j = 0; j < unit.diagram_ports.length; j++) {
          var port = unit.diagram_ports[j];
          port['x'] = icon.fixedPortLocation.x;
          port['y'] = icon.fixedPortLocation.y;
        }
      }
    }
  }

  defsElem.append('circle')
    .attr('id', 'locationAddressDef')
    .attr('r', 12)
    .attr('class', 'locationAddress');

  defsElem.append('g')
    .attr('id', 'speakerDef')
    .attr('class', 'speaker')
    .append('path')
    .attr('class', 'speaker')
    .attr('d', 'M10 10 L10 20 L30 20 L30 10 L40 0 L0 0 L10 10');

  defsElem.append('g')
    .attr('id', 'lightDef')
    .attr('class', 'light')
    .append('path')
    .attr('class', 'light')
    .attr('d', 'M22 8.51v1.372h-2.538c.02-.223.038-.448.038-.681 0-.237-.017-.464-.035-.69h2.535zm-10.648-6.553v-1.957h1.371v1.964c-.242-.022-.484-.035-.726-.035-.215 0-.43.01-.645.028zm5.521 1.544l1.57-1.743 1.019.918-1.603 1.777c-.25-.297-.593-.672-.986-.952zm-10.738.952l-1.603-1.777 1.019-.918 1.57 1.743c-.392.28-.736.655-.986.952zm-1.597 5.429h-2.538v-1.372h2.535c-.018.226-.035.454-.035.691 0 .233.018.458.038.681zm9.462 9.118h-4c-.276 0-.5.224-.5.5s.224.5.5.5h4c.276 0 .5-.224.5-.5s-.224-.5-.5-.5zm0 2h-4c-.276 0-.5.224-.5.5s.224.5.5.5h4c.276 0 .5-.224.5-.5s-.224-.5-.5-.5zm.25 2h-4.5l1.188.782c.154.138.38.218.615.218h.895c.234 0 .461-.08.615-.218l1.187-.782zm3.75-13.799c0 3.569-3.214 5.983-3.214 8.799h-1.989c-.003-1.858.87-3.389 1.721-4.867.761-1.325 1.482-2.577 1.482-3.932 0-2.592-2.075-3.772-4.003-3.772-1.925 0-3.997 1.18-3.997 3.772 0 1.355.721 2.607 1.482 3.932.851 1.478 1.725 3.009 1.72 4.867h-1.988c0-2.816-3.214-5.23-3.214-8.799 0-3.723 2.998-5.772 5.997-5.772 3.001 0 6.003 2.051 6.003 5.772z');

  var mspGroup = defsElem.append('g')
    .attr('id', 'mspDef')
    .attr('class', 'msp');
};

/**
 * Initialize line marker data. If custom line markers are needed for special
 * port connection types, they have to be added here.
 */
SbdDrawer.prototype.initLineMarker = function () {
  var that = this;
  this.lineMarker = {
    get: function (d) {
      if (this.hasOwnProperty(d.type)) {
        return that.lineMarker[d.type];
      } else {
        return that.lineMarker.default(d.type);
      }
    },
    ethernet: {
      label: 'E',
      x: -11,
      y: -11,
      width: 22,
      height: 22,
      rx: 11,
      ry: 11,
      rotateToLine: false
    },
    analogaudio: {
      label: 'audio',
      x: -23,
      y: -6,
      width: 46,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    audiopower: {
      label: 'audio pwr',
      x: -28,
      y: -6,
      width: 50,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    discrete: {
      label: 'discr',
      x: -23,
      y: -6,
      width: 46,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    resistor: {
      label: 'resistor',
      x: -26,
      y: -6,
      width: 50,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    serialvideo: {
      label: 'svl',
      x: -15,
      y: -6,
      width: 30,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    hdbaset: {
      label: 'HDBaseT',
      x: -23,
      y: -8,
      width: 46,
      height: 15,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    dc28v: {
      label: '28V',
      x: -15,
      y: -6,
      width: 30,
      height: 12,
      rx: 6,
      ry: 6,
      rotateToLine: true
    },
    default: function (type) {
      return {
        label: type,
        x: -(10 + type.length * 5) / 2,
        y: -8,
        width: 10 + type.length * 5,
        height: 15,
        rx: 6,
        ry: 6,
        rotateToLine: true
      };
    }
  };
};

/**
 * Returns true if an icon should be used for the provided unit instead of the
 * standard box.
 *
 * @param unit
 * @returns {boolean}
 */
SbdDrawer.prototype.isIconizedUnit = function (unit) {
  return unit.icon_id != null
    && this.getIcon(unit) != null;
};

/**
 * Gets the icon that should be used for a unit.
 *
 * @param unit
 * @returns {*}
 */
SbdDrawer.prototype.getIcon = function (unit) {
  return this.icons[unit.icon_id];
};

/**
 * Draws the units.
 *
 * @param units
 */
SbdDrawer.prototype.drawUnits = function (units) {
  var that = this;
  var unitGroup = this.svg.selectAll('g.unit')
    .data(units).enter().append('g')
    .attr("id", function (d) {
      return d.id + 'group';
    })
    .attr('class', 'unit')
    .attr("transform", function (d) {
      return "translate(" + d.x + "," + d.y + ")";
    });

  unitGroup.append('title').text(function (d) {
    return d.label + "\nP/N: " + d.partnumber + "\n"
      + d.description;
  });

  unitGroup.call(this.d3.drag()
    .on('drag', function (d) {
      that.sbd.onDragUnit(d, that.d3.event.x, that.d3.event.y);
    })
    .on('end', function (d) {
      that.sbd.onDragUnitEnd(d);
    }));

  this.drawUnitBoxes(unitGroup);
  this.drawUnitLabels(unitGroup);
  this.drawUnitLocationAddress(unitGroup);
  this.drawUnitResizeHandles(unitGroup);
  this.drawPorts(unitGroup);
};

/**
 * Returns the unit label truncated to fit the bounding box. Implemented as an approximation
 * since using a textPath did not work when used on the CAT ui app.
 *
 * @param unit
 * @returns string
 */
SbdDrawer.prototype.getClippedUnitLabel = function(unit) {
  var maxCharLength = Math.floor(unit.width / 7);
  var label = unit.label;
  if (unit.label.length > maxCharLength) {
    label = unit.label.substring(0, maxCharLength-2) + '..';
  }
  return label;
};

/**
 * Updates unit locations and sizes.
 *
 * @param units
 * @param portConnections
 */
SbdDrawer.prototype.updateUnits = function (units, portConnections) {
  var unitGroup = this.svg.selectAll('g.unit')
    .data(units);

  unitGroup.attr("transform", function (d) {
    return "translate(" + d.x + "," + d.y + ")";
  });

  this.updateUnitIconOrientation(units);
  this.updateUnitSizes(units);
  this.updatePortLocations(units, portConnections);
};

/**
 * Updates the icon orientation of iconized units.
 *
 * @param units
 */
SbdDrawer.prototype.updateUnitIconOrientation = function (units) {
  var that = this;
  this.svg.selectAll('g.unit')
    .data(units)
    .filter(function (d) {
      return that.isIconizedUnit(d);
    })
    .selectAll('use')
    .attr('transform', function (d) {
      var transform = '';
      if (d.icon.scale) {
        transform = 'scale(' + d.icon.scale + ') ';
      }
      var rotationAngle = 0;
      if (d.icon.autoRotate && d.diagram_ports.length > 0) {
        var dx = Math.sign((d.width / 2) - d.diagram_ports[0].x);
        var dy = Math.sign((d.height / 2) - d.diagram_ports[0].y);
        rotationAngle = dy > 0 ? 180 : 0;
      }
      return transform + 'rotate(' + rotationAngle + ','
        + (d.width / 2 / (d.icon.scale ? d.icon.scale : 1)) + ','
        + (d.height / 2 / (d.icon.scale ? d.icon.scale : 1)) + ')';
    });
};

/**
 * Updates the unit sizes.
 *
 * @param units
 */
SbdDrawer.prototype.updateUnitSizes = function (units) {
  var unitGroups = this.svg.selectAll('g.unit')
    .data(units);

  var that = this;

  unitGroups.selectAll('rect.unit')
    .attr('width', function (d) {
      return d.width;
    })
    .attr('height', function (d) {
      return d.height;
    });

  unitGroups.selectAll('text.unit-label')
    .attr('x', function (d) {
      return d.width / 2;
    })
    .attr('y', function (d) {
      return d.height / 2;
    })
    .text(function (d) {
      return that.getClippedUnitLabel(d);
    });

  unitGroups.selectAll('rect.resize-handle')
    .attr('x', function (d) {
      return d.width;
    })
    .attr('y', 0)
    .attr('width', 5)
    .attr('height', function (d) {
      return d.height;
    });
};

/**
 * Draws the boxes for non-iconized units and the appropriate def elements
 * for iconized units.
 *
 * @param unitGroup
 */
SbdDrawer.prototype.drawUnitBoxes = function (unitGroup) {
  var that = this;
  unitGroup.filter(function (d) {
    return !that.isIconizedUnit(d);
  })
    .append('rect')
    .attr("id", function (d) {
      return d.id;
    })
    .attr('width', function (d) {
      return d.width;
    })
    .attr('height', function (d) {
      return d.height;
    })
    .attr('class', function(d) {
      return 'unit' + (d.external ? ' unit-external' : '');
    })
    .on('dblclick', function(d) {
      that.sbd.unitDblClick(d);
    });

  unitGroup.filter(function (d) {
    return that.isIconizedUnit(d);
  })
    .append('use')
    .attr('xlink:href', function (d) {
      return '#' + d.icon.defId;
    })
    .attr('transform', function (d) {
      var transform = '';
      if (d.icon.scale) {
        transform = 'scale(' + d.icon.scale + ') ';
      }
      var rotationAngle = 0;
      if (d.icon.autoRotate && d.diagram_ports.length > 0) {
        var dx = Math.sign((d.width / 2) - d.diagram_ports[0].x);
        var dy = Math.sign((d.height / 2) - d.diagram_ports[0].y);
        rotationAngle = dy > 0 ? 180 : 0;
      }
      return transform + 'rotate(' + rotationAngle + ','
        + (d.width / 2 / (d.icon.scale ? d.icon.scale : 1)) + ','
        + (d.height / 2 / (d.icon.scale ? d.icon.scale : 1)) + ')';
    }).on('dblclick', function(d) {
    that.sbd.unitDblClick(d);
  });
};

/**
 * Adds resize handles to units to enable changing the unit width.
 *
 * @param unitGroup
 */
SbdDrawer.prototype.drawUnitResizeHandles = function (unitGroup) {
  var that = this;
  unitGroup.append('rect')
    .attr('class', 'resize-handle')
    .attr('x', function (d) {
      return d.width;
    })
    .attr('y', 0)
    .attr('width', 5)
    .attr('height', function (d) {
      return d.height;
    })
    .call(this.d3.drag()
      .on('drag', function (d) {
        that.sbd.onDragUnitResizeHandle(d, that.d3.event.dx, 0);
      })
      .on('end', function (d) {
        that.sbd.onDragUnitResizeHandleEnd(d);
      })
    );
};

/**
 * Draws the unit labels centered into the unit.
 *
 * @param unitGroup
 */
SbdDrawer.prototype.drawUnitLabels = function (unitGroup) {
  var that = this;
  unitGroup.append('text')
    .attr('class', function (d) {
      return 'unit-label'
        + (d.icon_id != null ? ' icon-label-' + d.icon_id : '');
    })
    .attr('x', function (d) {
      return d.width / 2;
    })
    .attr('y', function (d) {
      return d.height / 2;
    })
    .style('dominant-baseline', 'middle')
    .style('text-anchor', 'middle')
    .text(function (d) {
      return that.getClippedUnitLabel(d);
    });
};

/**
 * Draws a circle to indicate the location address (if the unit has one).
 *
 * @param unitGroup
 */
SbdDrawer.prototype.drawUnitLocationAddress = function (unitGroup) {
  var locAddrGroup = unitGroup
    .append('g')
    .style('opacity', function (d) {
      return d.location_address != null ? 1 : 0;
    });

  locAddrGroup.append('use')
    .attr('xlink:href', '#locationAddressDef');

  locAddrGroup.append('text')
    .style('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .text(function (d) {
      return d.location_address;
    });
};

/**
 * Shows or hides additional text for a unit. The text is rendered above
 * the unit on the right side. The text to render is specified with a getter
 * function that takes a unit as the only argument.
 *
 * @param units all units
 * @param unitAttrGetter getter function for the text to be displayed
 * @param show true to show text, false to hide text
 */
SbdDrawer.prototype.showUnitInfo = function(units, unitAttrGetter, show) {
  var unitGroup = this.svg.selectAll('g.unit')
    .data(units);

  if (show) {
    unitGroup.append('text')
      .attr('class', 'unit-info')
      .attr('transform', function (d) {
        var translateX = d.width;
        return 'translate(' + translateX + ',0)';
      })
      .attr('text-anchor', 'end')
      .style('dominant-baseline', 'text-after-edge')
      .text(unitAttrGetter);
  } else {
    unitGroup.selectAll('text.unit-info')
      .remove();
  }
};

/**
 * Draws the ports of a unit.
 *
 * @param unitGroup
 */
SbdDrawer.prototype.drawPorts = function (unitGroup) {
  var that = this;
  var portGroup = unitGroup.selectAll('g.port')
    .data(function (d) {
      return d.diagram_ports;
    })
    .enter()
    .append('g')
    .attr('class', 'port')
    .style('opacity', function (d) {
      return d.portConnections.length > 0 || d.connected_to_virtual_port ? 1 : 0;
    })
    .attr("transform", function (d) {
      return "translate(" + d.x + "," + d.y + ")";
    });

  portGroup
    .call(this.d3.drag()
      .on('drag', function (d) {
        that.sbd.onDragPort(d, that.d3.event.x, that.d3.event.y);
        this.setAttribute('class', 'port port-drag');
      })
      .on('end', function (d) {
        this.setAttribute('class', 'port');
      }));

  portGroup.append('title').text(function (d) {
    return d.name_and_unit + "\nID: " + d.id;
  });

  this.drawPortMarkers(portGroup);
  this.drawPortLabelTexts(portGroup);
  this.drawVirtualPortConnections(portGroup);
};

/**
 * Draws lines and markers for virtual port connections, i.e. ports that are
 * connected to a virtual port.
 *
 * @param portGroup
 */
SbdDrawer.prototype.drawVirtualPortConnections = function (portGroup) {
  var virtualPortGroup = portGroup.filter(function (d) {
    return d.connected_to_virtual_port === true;
  });

  virtualPortGroup.append('line')
    .attr('class', function (d) {
      return 'portConnectionVirtual portConnection-' + d.type;
    });

  var lineMarkerGroup = virtualPortGroup.append('g')
    .attr('class', function (d) {
      return 'portConnectionVirtual portConnection-' + d.type;
    });

  var lineMarker = this.lineMarker;
  lineMarkerGroup.append('rect')
    .attr('class', 'portConnection')
    .attr('x', function (d) {
      return lineMarker.get(d).x;
    })
    .attr('y', function (d) {
      return lineMarker.get(d).y;
    })
    .attr('width', function (d) {
      return lineMarker.get(d).width;
    })
    .attr('height', function (d) {
      return lineMarker.get(d).height;
    })
    .attr('rx', function (d) {
      return lineMarker.get(d).rx;
    })
    .attr('ry', function (d) {
      return lineMarker.get(d).ry;
    });

  lineMarkerGroup.append('text')
    .attr('class', function (d) {
      return 'portConnectionLabel portConnectionLabel-' + d.type;
    })
    .style('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .text(function (d) {
      return lineMarker.get(d).label;
    });

  this.updateVirtualPortConnectionDirection(portGroup);
};

/**
 * Updates the marker locations of virtual port connections.
 *
 * @param portGroup
 */
SbdDrawer.prototype.updateVirtualPortConnectionMarkerLocations = function (portGroup) {
  var dx = this.sbd.virtualPortConnectionWidth / 2;
  var dy = this.sbd.virtualPortConnectionHeight / 2;

  portGroup.selectAll('g.portConnectionVirtual')
    .attr('transform', function (d) {
      var x = (d.x === d.parent.width) ? dx : (d.x === 0) ? -dx : 0;
      var y = (d.y === d.parent.height) ? dy : (d.y === 0) ? -dy : 0;
      return "translate(" + x + "," + y + ")";
    });
};

/**
 * Updates the direction of virtual port connection lines so that they are
 * always facing away from the unit.
 *
 * @param portGroup
 */
SbdDrawer.prototype.updateVirtualPortConnectionDirection = function (portGroup) {
  var dx = this.sbd.virtualPortConnectionWidth;
  var dy = this.sbd.virtualPortConnectionHeight;

  var decode = function(expr, search1, result1, search2, result2, defaultVal) {
    return expr === search1 ? result1 :
      expr === search2 ? result2 : defaultVal;
  }

  // Draw line from input port to output port
  portGroup.selectAll('line.portConnectionVirtual')
    .attr('x1', function(d) {
      return d.output ? 0 : decode(d.x, d.parent.width, dx, 0, -dx, 0);
    })
    .attr('y1', function(d) {
      return d.output ? 0 : decode(d.y, d.parent.height, dy, 0, -dy, 0);
    })
    .attr('x2', function(d) {
      return d.output ? decode(d.x, d.parent.width, dx, 0, -dx, 0) : 0;
    })
    .attr('y2', function(d) {
      return d.output ? decode(d.y, d.parent.height, dy, 0, -dy, 0) : 0;
    });

  this.updateVirtualPortConnectionMarkerLocations(portGroup);
};

/**
 * Updates the port locations.
 *
 * @param units
 * @param portConnections
 */
SbdDrawer.prototype.updatePortLocations = function (units, portConnections) {
  var portGroup = this.d3.selectAll('g.unit')
    .data(units)
    .selectAll('g.port')
    .data(function (d) {
      return d.diagram_ports;
    })
    .attr('transform', function (port) {
      return 'translate(' + port.x + ',' + port.y + ')';
    });

  this.updatePortLabelTextLocations(units);
  this.updatePortConnectionLocations(portConnections);
  this.updateVirtualPortConnectionDirection(portGroup);
};

/**
 * Converts a port type, port connection type, etc. to a valid css class by
 * removing all white spaces and using consistent lower case.
 *
 * @param type
 * @returns {string}
 */
SbdDrawer.prototype.typeToCss = function (type) {
  if (typeof type === 'string' || type instanceof String) {
    return type.replace(/\s/g, '').toLowerCase();
  }
};

/**
 * Draws markers for a port.
 *
 * @param portGroup
 */
SbdDrawer.prototype.drawPortMarkers = function (portGroup) {
  var that = this;
  portGroup.append('circle')
    .attr('class', function (port) {
      return 'port port-' + port.type;
    })
    .attr('r', 5)
    .on('dblclick', function(d) {
      that.sbd.portDblClick(d);
    });
};

/**
 * Draws the label of a port, inside the unit rectangle.
 *
 * @param portGroup
 */
SbdDrawer.prototype.drawPortLabelTexts = function (portGroup) {
  var that = this;
  portGroup.filter(function (d) {
    return !that.isIconizedUnit(d.parent);
  })
    .append('text')
    .attr('class', 'portLabel')
    .attr('x', function (port) {
      return that.getPortLabelCoords(port).x;
    })
    .attr('y', function (port) {
      return that.getPortLabelCoords(port).y;
    })
    .attr('text-anchor', function (port) {
      return that.getPortLabelBaseline(port).x;
    })
    .style('dominant-baseline', function (port) {
      return that.getPortLabelBaseline(port).y;
    })
    .text(function (port) {
      return port.label;
    });
};


/**
 * Updates the label locations for port labels.
 *
 * @param units
 */
SbdDrawer.prototype.updatePortLabelTextLocations = function (units) {
  var that = this;
  var portLabelTexts = this.d3.selectAll('g.unit')
    .data(units)
    .selectAll('g.port')
    .data(function (d) {
      return d.diagram_ports;
    })
    .selectAll('text.portLabel')
    .attr('x', function (port) {
      return that.getPortLabelCoords(port).x;
    })
    .attr('y', function (port) {
      return that.getPortLabelCoords(port).y;
    })
    .attr('text-anchor', function (port) {
      return that.getPortLabelBaseline(port).x;
    })
    .style('dominant-baseline', function (port) {
      return that.getPortLabelBaseline(port).y;
    });
};

/**
 * Draws port connections.
 *
 * @param portConnections
 */
SbdDrawer.prototype.drawPortConnections = function (portConnections) {
  this.svg.selectAll('line.portConnection')
    .data(portConnections).enter()
    .append('line')
    .attr('class', function (d) {
      return 'portConnection' +
          (d.to_be_removed === true ? ' portConnection-removed ' : '') +
          ' portConnection-' + d.type;
    });

  this.drawPortConnectionMarkers(portConnections);
  this.updatePortConnectionLocations(portConnections);
};

/**
 * Updates port connection lines.
 *
 * @param portConnections
 */
SbdDrawer.prototype.updatePortConnectionLocations = function (portConnections) {
  this.svg.selectAll('line.portConnection')
    .data(portConnections)
    .attr('x1', function (d) {
      return d.sourcePort.parent.x
        + d.sourcePort.x;
    })
    .attr('y1', function (d) {
      return d.sourcePort.parent.y
        + d.sourcePort.y;
    })
    .attr('x2', function (d) {
      return d.destPort.parent.x
        + d.destPort.x;
    })
    .attr('y2', function (d) {
      return d.destPort.parent.y
        + d.destPort.y;
    });

  this.updatePortConnectionMarkerLocations(portConnections);
};

/**
 * Draws the port connection markers which indicate the type of port connection.
 *
 * @param portConnections
 */
SbdDrawer.prototype.drawPortConnectionMarkers = function (portConnections) {
  var lineMarkerGroup = this.svg.selectAll('g.portConnection')
    .data(portConnections).enter()
    .append('g')
    .attr('class', function (d) {
      return 'portConnection portConnection-' + d.type;
    });

  var that = this;
  var lineMarker = this.lineMarker;
  lineMarkerGroup.append('rect')
    .attr('class', function(d) {
      return 'portConnection' + (d.editable ? ' portConnection-editable' : '');
    })
    .attr('x', function (d) {
      return lineMarker.get(d).x;
    })
    .attr('y', function (d) {
      return lineMarker.get(d).y;
    })
    .attr('width', function (d) {
      return lineMarker.get(d).width;
    })
    .attr('height', function (d) {
      return lineMarker.get(d).height;
    })
    .attr('rx', function (d) {
      return lineMarker.get(d).rx;
    })
    .attr('ry', function (d) {
      return lineMarker.get(d).ry;
    })
    .on('dblclick', function(d) {
      that.sbd.portConnectionDblClick(d);
    });

  lineMarkerGroup.append('text')
    .attr('class', function (d) {
      return 'portConnectionLabel portConnectionLabel-' + d.type;
    })
    .style('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .text(function (d) {
      return lineMarker.get(d).label;
    });

  this.updatePortConnectionMarkerLocations(portConnections);
};

/**
 * Updates the port connection marker locations. Markers are centered on the
 * port connection line.
 *
 * @param portConnections
 */
SbdDrawer.prototype.updatePortConnectionMarkerLocations = function (portConnections) {
  var that = this;
  this.svg.selectAll('g.portConnection')
    .data(portConnections)
    .attr('transform', function (d) {
      var x0 = d.sourcePort.parent.x + d.sourcePort.x;
      var x1 = d.destPort.parent.x + d.destPort.x;
      var y0 = d.sourcePort.parent.y + d.sourcePort.y;
      var y1 = d.destPort.parent.y + d.destPort.y;

      // Translate to center of line
      var translate = 'translate('
        + that.helper.getCenter(x0, x1)
        + ','
        + that.helper.getCenter(y0, y1)
        + ')';

      var rotationAngle = 0;
      if (that.lineMarker.get(d).rotateToLine) {
        // Rotate to line orientation, always left to right
        var dx = x1 - x0 > 0 ? x1 - x0 : x0 - x1;
        var dy = x1 - x0 > 0 ? y1 - y0 : y0 - y1;
        rotationAngle = (Math.abs(dx) < 0.01) ?
          (-Math.PI / 2) :
          Math.atan(dy / dx);
      }
      var rotate = 'rotate(' + (rotationAngle * 180 / Math.PI) + ')';

      return translate + ' ' + rotate;
    });
};

/**
 * Get the port label coordinates. When the port location is closer to a corner than
 * PORT_LABEL_MIN_DIST, the port label keeps this distance from the corner so that
 * the label stays within the unit rectangle.
 *
 * @param port
 * @returns {{x: number, y: number}}
 */
SbdDrawer.prototype.getPortLabelCoords = function (port) {
  var PADDING = this.PORT_LABEL_MIN_DIST;
  return {
    x: port.x < PADDING && port.x > 0 ? PADDING - port.x :
      port.x > port.parent.width - PADDING && port.x < port.parent.width ? port.parent.width - PADDING - port.x : 0,
    y: port.y < PADDING && port.y > 0 ? PADDING - port.y :
      port.y > port.parent.height - PADDING && port.y < port.parent.height ? port.parent.height - PADDING - port.y : 0
  }
};

/**
 * Get the baselines for the port label text. The baseline is chosen so that
 * the label is drawn within the unit rectangle.
 *
 * @param port
 * @returns {{x: string, y: string}}
 */
SbdDrawer.prototype.getPortLabelBaseline = function(port) {
  return {
    x: port.x == 0 ? 'start' :
      port.x == port.parent.width ? 'end' : 'middle',
    y: port.y == 0 ? 'text-before-edge' :
      port.y == port.parent.height ? 'text-after-edge' : 'middle'
  }
}

/**
 * Collection of helper methods for drawing System Block Diagrams.
 *
 * @constructor
 */
function SbdHelper() {
  /**
   * Returns the center of two values.
   *
   * @param x1
   * @param x2
   * @returns {number}
   */
  this.getCenter = function (x1, x2) {
    return (x1 + x2) / 2;
  }

  /**
   * Clips the provided coordinate if necessary to ensure that it is in the
   * provided range.
   *
   * @param coord the coordinate
   * @param minVal the minimum allowed value
   * @param maxVal the maximum allowed value
   * @returns {number} the clipped coordinate which is >= minVal and <= maxVal.
   */
  this.clipCoord = function (coord, minVal, maxVal) {
    return Math.max(minVal, Math.min(maxVal, coord));
  };

  /**
   * Returns true if the given point is on the rectangle specified by x0, y0,
   * width and height.
   *
   * @param x
   * @param y
   * @param x0
   * @param y0
   * @param width
   * @param height
   * @returns {boolean}
   */
  this.isPointOnRect = function (x, y, x0, y0, width, height) {
    return ((x == x0 || x == (x0 + width))
      && y >= y0 && y <= (y0 + height))
      || ((y == y0 || y == (y0 + height))
        && x >= x0 && x <= (x0 + width));
  }

  /**
   * Computes the point on a rectangle (specified by x0, y0, width and height)
   * which is closest to a given point.
   *
   * @param x
   * @param y
   * @param x0
   * @param y0
   * @param width
   * @param height
   * @returns {{x: number, y: number}}
   */
  this.nearestOnRect = function (x, y, x0, y0, width, height) {
    var x1 = x0 + width;
    var y1 = y0 + height;
    /* Distance to left/top/right/bottom */
    var minDists = [x - x0,
      y - y0,
      x - x1,
      y - y1];
    var minDistsSqrd = minDists.map(function (d1) {
      return d1 * d1;
    });
    if (y < y0) {
      minDistsSqrd[0] += (y - y0) * (y - y0);
      minDistsSqrd[2] += (y - y0) * (y - y0);
    }
    if (y > y1) {
      minDistsSqrd[0] += (y - y1) * (y - y1);
      minDistsSqrd[2] += (y - y1) * (y - y1);
    }
    if (x < x0) {
      minDistsSqrd[1] += (x - x0) * (x - x0);
      minDistsSqrd[3] += (x - x0) * (x - x0);
    }
    if (x > x1) {
      minDistsSqrd[1] += (x - x1) * (x - x1);
      minDistsSqrd[3] += (x - x1) * (x - x1);
    }
    var anchorX = 0;
    /* left */
    var anchorY = 1;
    /* top */
    if (minDistsSqrd[2] < minDistsSqrd[0]) {
      anchorX = 2;
      /* right */
    }
    if (minDistsSqrd[3] < minDistsSqrd[1]) {
      anchorY = 3;
      /* bottom */
    }
    if (minDistsSqrd[anchorX] < minDistsSqrd[anchorY]) {
      return {
        x: this.clipCoord(x - minDists[anchorX], x0, x1),
        y: this.clipCoord(y, y0, y1)
      };
    } else {
      return {
        x: this.clipCoord(x, x0, x1),
        y: this.clipCoord(y - minDists[anchorY], y0, y1)
      }
    }
  };
};

/**
 * Provides a menu with buttons to a system block diagram. The buttons are
 * rendered onto the svg.
 *
 * @param sbd
 * @constructor
 */
function SbdMenu(sbd) {
  this.sbd = sbd;
  this.bounds = {x: 0, y: 0};
}

/**
 * Renders the menu.
 */
SbdMenu.prototype.render = function () {
  var that = this;

  this.zoomOutButton = {
    type: 'click',
    label: '-',
    tooltip: 'Zoom out',
    onClick: function () {
      that.sbd.zoomOut();
    }
  };

  this.zoomInButton = {
    type: 'click',
    label: '+',
    tooltip: 'Zoom in',
    onClick: function () {
      that.sbd.zoomIn();
    }
  };

  this.autoSetPortLocationsButton = {
    type: 'toggle',
    label: '|_',
    tooltip: 'Auto-layout port connections',
    pressed: false,
    onClick: function (pressed) {
      that.sbd.enableAutoSetPortLocations(pressed);
    }
  };

  this.saveButton = {
    type: 'click',
    label: 'Save',
    tooltip: 'Save block diagram layout',
    onClick: function () {
      that.sbd.save();
    }
  };

  this.buttons = [this.zoomOutButton, this.zoomInButton,
    this.autoSetPortLocationsButton, this.saveButton];
  this.drawButtons();
};

/**
 * Updates button positions and sizes dependent on the given zoom factor
 * so that the button positions and sizes remain constant from a user point
 * of view.
 *
 * @param zoomFactor
 */
SbdMenu.prototype.updatePositionAndSize = function (zoomFactor) {
  var buttonGroup = this.sbd.svg.selectAll('g.button')
    .data(this.buttons);

  var that = this;

  if (isNaN(zoomFactor)) {
    zoomFactor = 1;
  }

  this.bounds = {x: 0, y: 0};

  // Update position
  buttonGroup.attr('transform', function (d, i) {
    var x = (30 + 55 * i) / zoomFactor;
    var y = 20.0 / zoomFactor;
    that.bounds.x = Math.max(x + 25, that.bounds.x);
    that.bounds.y = Math.max(y + 15, that.bounds.y);
    return 'translate(' + x + ',' + y + ')';
  });

  // Update size
  buttonGroup.selectAll('rect')
    .attr('x', -25 / zoomFactor)
    .attr('y', -15 / zoomFactor)
    .attr('width', 50 / zoomFactor)
    .attr('height', 30 / zoomFactor)
    .attr('rx', 0 / zoomFactor)
    .attr('ry', 0 / zoomFactor)
    .attr('stroke-width', 1 / zoomFactor);

  // Update font size
  buttonGroup.selectAll('text')
    .attr('font-size', 20.0 / zoomFactor);
};

/**
 * Draws the menu buttons.
 *
 * @param buttons
 */
SbdMenu.prototype.drawButtons = function (buttons) {
  var that = this;

  var buttonGroup = this.sbd.svg.selectAll('g.button')
    .data(this.buttons).enter()
    .append('g')
    .attr('class', function (d) {
      return 'button button-' + d.type;
    })
    .attr('cursor', 'pointer')
    .on('click', function (d) {
      if (d.type === 'click') {
        d.onClick();
      } else if (d.type === 'toggle') {
        d.pressed = !d.pressed;
        this.setAttribute('class', 'button button-toggle' + (d.pressed ? ' button-pressed' : ''));
        d.onClick(d.pressed);
      } else {
        console.error('Unknown button type: ' + d.type);
      }
    });

  buttonGroup.append('rect');
  buttonGroup.append('text')
    .style('dominant-baseline', 'middle')
    .attr('text-anchor', 'middle')
    .text(function (d) {
      return d.label;
    });

  // Set tooltip
  buttonGroup.append('title')
    .text(function (d) {
      return d.tooltip;
    });

  this.updatePositionAndSize(this.sbd.zoomFactor);
};

/**
 * Updates the visible state of all toggle buttons.
 */
SbdMenu.prototype.updateButtonStates = function () {
  this.sbd.svg.selectAll('g.button')
    .data(this.buttons)
    .filter(function (d) {
      return d.type === 'toggle';
    })
    .attr('class', function (d) {
      return 'button button-toggle' + (d.pressed ? ' button-pressed' : '');
    });
};



//////////////////
// WEBPACK FOOTER
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/lib/blockdiagram-editor/system-block-diagram.js
// module id = ../../../../../src/app/lib/blockdiagram-editor/system-block-diagram.js
// module chunks = main